import React, { useEffect } from "react"
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep"
import PowerIcon from "@material-ui/icons/Power"
import SaveIcon from "@material-ui/icons/Save"
import WifiIcon from "@material-ui/icons/Wifi"
import { useSnackbar } from "../../../utils/notificacoes"
import { useIntegracaoFoodyDeliveryContext } from "../context/useIntegracaoFoodyDeliveryContext"
import { IntegracaoFoodyDeliveryActionsTypes } from "../context/integracaoFoodyDeliveryActions"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderLeft: {
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    iconErros: {
        color: "var(--cor-erro)",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subTitle: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(3),
    },
    fieldLabel: {
        color: "#2c3e50",
        fontWeight: 500,
        marginBottom: theme.spacing(0.5),
        display: "block",
    },
    hint: {
        color: "#7f8c8d",
        fontSize: "0.78rem",
        marginTop: theme.spacing(0.5),
    },
    buttonRow: {
        display: "flex",
        gap: theme.spacing(1.5),
        flexWrap: "wrap",
        marginTop: theme.spacing(2.5),
    },
    btnSalvar: {
        borderRadius: 25,
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.3)",
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    btnTestar: {
        borderRadius: 25,
        backgroundColor: "#1976d2",
        color: "white",
        "&:hover": { backgroundColor: "#1565c0" },
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    mensagemTeste: {
        color: "#2c3e50",
        marginTop: theme.spacing(1.5),
        fontSize: "0.9rem",
    },
    tableContainer: {
        maxHeight: 360,
        overflowX: "auto",
    },
    codeCell: {
        fontFamily: "monospace",
        fontSize: "0.8rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    semErros: {
        color: "#4caf50",
        fontWeight: 500,
        padding: theme.spacing(2, 0),
    },
    notaRodape: {
        color: "#7f8c8d",
        fontSize: "0.78rem",
        marginTop: theme.spacing(1),
    },
}))

function statusChip(ativa: boolean | null) {
    if (ativa === null) return <Chip label="Não testada" size="small" />
    if (ativa) return <Chip label="Ativa" size="small" style={{ backgroundColor: "var(--cor-botao-salvar)", color: "white" }} />
    return <Chip label="Inativa" size="small" style={{ backgroundColor: "var(--cor-erro)", color: "white" }} />
}

export function IntegracaoFoodyDeliveryForm() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento, salvarToken, testarIntegracao, limparErros } =
        useIntegracaoFoodyDeliveryContext()
    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const handleLimparErros = () => {
        if (!window.confirm("Limpar histórico de erros?")) return
        limparErros()
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integração Foody Delivery
                </Typography>
                <Typography variant="body2" className={classes.subTitle}>
                    Configure o token de acesso da Foody Delivery e teste a conexão. Erros recentes ficam disponíveis
                    abaixo para diagnóstico.
                </Typography>

                {/* Card Credenciais */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardHeaderLeft}>
                            <WifiIcon className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Credenciais
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={classes.cardContent}>
                        <label className={classes.fieldLabel}>
                            Token de autenticação Foody Delivery
                        </label>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Cole aqui o Bearer/token fornecido pela Foody"
                            value={dados.token}
                            onChange={e => {
                                const value = e.target.value
                                dispatch({
                                    type: IntegracaoFoodyDeliveryActionsTypes.ATUALIZAR_TOKEN,
                                    payload: value,
                                })
                            }}
                        />
                        <Typography className={classes.hint}>
                            Endpoint base: <code>https://app.foodydelivery.com/rest/1.2</code>
                        </Typography>

                        <Box className={classes.statusRow}>
                            <Typography variant="body2" style={{ color: "#2c3e50" }}>
                                Status:
                            </Typography>
                            {statusChip(dados.integracaoAtiva)}
                        </Box>

                        {dados.mensagemTeste && (
                            <Typography className={classes.mensagemTeste}>
                                <strong>Resultado do último teste:</strong> {dados.mensagemTeste}
                            </Typography>
                        )}

                        <Box className={classes.buttonRow}>
                            <Button
                                variant="contained"
                                className={classes.btnSalvar}
                                startIcon={<SaveIcon />}
                                onClick={salvarToken}
                            >
                                Salvar Token
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.btnTestar}
                                startIcon={<PowerIcon />}
                                onClick={testarIntegracao}
                            >
                                Testar Integração
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Card Erros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Box className={classes.cardHeaderLeft}>
                            <WifiIcon className={classes.iconErros} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Últimos erros da integração
                            </Typography>
                        </Box>
                        {dados.erros.length > 0 && (
                            <Tooltip title="Limpar histórico">
                                <IconButton size="small" onClick={handleLimparErros}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.erros.length === 0 ? (
                            <Typography className={classes.semErros}>
                                Nenhum erro recente registrado para este estabelecimento.
                            </Typography>
                        ) : (
                            <>
                                <TableContainer className={classes.tableContainer}>
                                    <Table
                                        size="small"
                                        stickyHeader
                                        style={{ width: "100%", minWidth: 470 }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ minWidth: 120 }}>Data/Hora</TableCell>
                                                <TableCell style={{ minWidth: 130 }}>Operação</TableCell>
                                                <TableCell>Mensagem</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dados.erros.map((erro, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell className={classes.codeCell} title={erro.data}>
                                                        <code>{erro.data}</code>
                                                    </TableCell>
                                                    <TableCell>{erro.operacao}</TableCell>
                                                    <TableCell className={classes.codeCell} title={erro.mensagem}>
                                                        <code>{erro.mensagem}</code>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography className={classes.notaRodape}>
                                    São exibidos os últimos 20 erros das chamadas à Foody (TTL 7 dias no Redis).
                                </Typography>
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
